import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Input,
  Modal,
  Progress,
  Select,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CopyOutlined, OpenAIOutlined, ReloadOutlined, TranslationOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import useHttpClient from "components/hooks/useHttpClient";
import { useTranslate } from "components/hooks/useTranslate";
import {
  ProductSearchResult,
  ProductSearchSelect,
} from "components/common/productSearchSelect";
import { ILanguage } from "interfaces";
import { useLocaleStore } from "stores/localeStore";
import { BaseKey } from "@refinedev/core";

type SpecificationLanguageValueItem = {
  languageId: number;
  locale: string;
  value: string;
  hasTranslation: boolean;
};

type SpecificationTranslationOverviewItem = {
  id: number;
  labelId: number;
  label: string;
  unitAbbreviation?: string | null;
  translations: SpecificationLanguageValueItem[];
};

type SpecificationTranslationOverviewResponse = {
  productId: number;
  specifications: SpecificationTranslationOverviewItem[];
};

type DraftRow = {
  specificationId: number;
  label: string;
  unitAbbreviation?: string | null;
  sourceValue: string;
  targetValue: string;
  hasTargetTranslation: boolean;
  dirty: boolean;
};

const getLanguageName = (language: ILanguage): string => {
  switch (language.locale) {
    case "sv":
      return "Svenska";
    case "da":
      return "Danska";
    case "no":
      return "Norska";
    default:
      return language.locale.toUpperCase();
  }
};

const getTranslation = (
  item: SpecificationTranslationOverviewItem,
  locale: string
): SpecificationLanguageValueItem | undefined =>
  item.translations.find((t) => t.locale === locale);

const formatProductLabel = (product: {
  id?: number | null;
  name?: string | null;
  mainItemNo?: string | null;
}): string => {
  const name = product.name?.trim() || `Produkt #${product.id ?? ""}`;
  return product.mainItemNo ? `${name} (${product.mainItemNo})` : name;
};

interface SpecificationTranslationDialogProps {
  productId: BaseKey;
  open: boolean;
  onClose: () => void;
  /** Called after save when the active product is still the page product. */
  onSaved?: () => void;
}

export const SpecificationTranslationDialog: React.FC<
  SpecificationTranslationDialogProps
> = ({ productId, open, onClose, onSaved }) => {
  const { languages, locale: activeLocale } = useLocaleStore();
  const { makeHttpRequestThrowErrors } = useHttpClient();
  const { translateSpecifications } = useTranslate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sourceLocale, setSourceLocale] = useState("sv");
  const [targetLocale, setTargetLocale] = useState(
    activeLocale && activeLocale !== "sv" ? activeLocale : "no"
  );
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [activeProductId, setActiveProductId] = useState<BaseKey>(productId);
  const [activeProductLabel, setActiveProductLabel] = useState(
    `Produkt #${productId}`
  );

  const languageOptions = useMemo(
    () =>
      languages.map((lang) => ({
        value: lang.locale,
        label: getLanguageName(lang),
      })),
    [languages]
  );

  const hasDirtyRows = rows.some((r) => r.dirty);

  const buildRows = (
    overview: SpecificationTranslationOverviewResponse,
    fromLocale: string,
    toLocale: string
  ): DraftRow[] =>
    overview.specifications.map((spec) => {
      const source = getTranslation(spec, fromLocale);
      const target = getTranslation(spec, toLocale);
      return {
        specificationId: spec.id,
        label: spec.label,
        unitAbbreviation: spec.unitAbbreviation,
        sourceValue: source?.value ?? "",
        targetValue: target?.hasTranslation ? target.value : "",
        hasTargetTranslation: !!target?.hasTranslation,
        dirty: false,
      };
    });

  const loadOverview = async (
    id: BaseKey,
    fromLocale: string,
    toLocale: string
  ) => {
    if (!id) return;
    setLoading(true);
    try {
      const data =
        (await makeHttpRequestThrowErrors(
          "GET",
          `/specifications/${id}/translations`
        )) as SpecificationTranslationOverviewResponse;

      setRows(buildRows(data, fromLocale, toLocale));
    } catch (error) {
      console.error(error);
      message.error("Kunde inte hämta översikter för översättningar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const nextSource = languages.some((l) => l.locale === "sv")
      ? "sv"
      : languages[0]?.locale ?? "sv";
    const nextTarget =
      activeLocale && activeLocale !== nextSource
        ? activeLocale
        : languages.find((l) => l.locale !== nextSource)?.locale ?? nextSource;

    setSourceLocale(nextSource);
    setTargetLocale(nextTarget);
    setActiveProductId(productId);
    setActiveProductLabel(`Produkt #${productId}`);
    void loadOverview(productId, nextSource, nextTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const completedCount = rows.filter((r) => r.targetValue?.trim()).length;
  const totalCount = rows.length;
  const progressPercent =
    totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

  const switchProduct = (product: ProductSearchResult) => {
    if (!product.id) return;

    const applySwitch = () => {
      setActiveProductId(product.id!);
      setActiveProductLabel(formatProductLabel(product));
      void loadOverview(product.id!, sourceLocale, targetLocale);
    };

    if (!hasDirtyRows) {
      applySwitch();
      return;
    }

    Modal.confirm({
      title: "Osparade ändringar",
      content:
        "Du har osparade översättningar. Vill du byta produkt utan att spara?",
      okText: "Byt produkt",
      cancelText: "Avbryt",
      onOk: applySwitch,
    });
  };

  const handleSourceChange = (locale: string) => {
    setSourceLocale(locale);
    void loadOverview(activeProductId, locale, targetLocale);
  };

  const handleTargetChange = (locale: string) => {
    setTargetLocale(locale);
    void loadOverview(activeProductId, sourceLocale, locale);
  };

  const updateTargetValue = (specificationId: number, value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.specificationId === specificationId
          ? { ...row, targetValue: value, dirty: true }
          : row
      )
    );
  };

  const copyFromSource = (specificationId: number) => {
    setRows((prev) =>
      prev.map((row) =>
        row.specificationId === specificationId
          ? {
              ...row,
              targetValue: row.sourceValue,
              dirty: true,
            }
          : row
      )
    );
  };

  const handleSave = async () => {
    const dirtyRows = rows.filter((r) => r.dirty);
    if (dirtyRows.length === 0) {
      message.info("Inga ändringar att spara");
      return;
    }

    setSaving(true);
    try {
      await makeHttpRequestThrowErrors(
        "PATCH",
        `/specifications/${activeProductId}/translations`,
        {
          locale: targetLocale,
          items: dirtyRows.map((r) => ({
            specificationId: r.specificationId,
            value: r.targetValue,
          })),
        }
      );
      message.success("Översättningar sparade");
      await loadOverview(activeProductId, sourceLocale, targetLocale);
      if (String(activeProductId) === String(productId)) {
        onSaved?.();
      }
    } catch (error) {
      console.error(error);
      message.error("Kunde inte spara översättningar");
    } finally {
      setSaving(false);
    }
  };

  const handleAiTranslate = () => {
    if (!activeProductId || targetLocale === "sv") {
      message.warning("Välj ett målspråk som inte är svenska");
      return;
    }
    translateSpecifications([activeProductId], [targetLocale]);
    message.info(
      "AI-översättningsjobb startat. Klicka Uppdatera när jobbet är klart."
    );
  };

  const handleRefresh = () => {
    const reload = () => {
      void loadOverview(activeProductId, sourceLocale, targetLocale);
    };

    if (!hasDirtyRows) {
      reload();
      return;
    }

    Modal.confirm({
      title: "Osparade ändringar",
      content:
        "Du har osparade översättningar. Vill du uppdatera och kasta dem?",
      okText: "Uppdatera",
      cancelText: "Avbryt",
      onOk: reload,
    });
  };

  const columns: ColumnsType<DraftRow> = [
    {
      title: "ATTRIBUT",
      dataIndex: "label",
      key: "label",
      width: "28%",
      render: (label: string, row) => (
        <Flex vertical gap={2}>
          <Typography.Text strong>{label}</Typography.Text>
          {row.unitAbbreviation ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.unitAbbreviation}
            </Typography.Text>
          ) : null}
        </Flex>
      ),
    },
    {
      title: getLanguageName({ id: 0, locale: sourceLocale }),
      dataIndex: "sourceValue",
      key: "sourceValue",
      width: "30%",
      render: (value: string) => (
        <Typography.Text type={value ? undefined : "secondary"}>
          {value || "—"}
        </Typography.Text>
      ),
    },
    {
      title: getLanguageName({ id: 0, locale: targetLocale }),
      dataIndex: "targetValue",
      key: "targetValue",
      render: (_: string, row) => {
        const empty = !row.targetValue?.trim();
        return (
          <Flex gap={8} align="center">
            <Input
              value={row.targetValue}
              placeholder="Skriv översättning..."
              onChange={(e) =>
                updateTargetValue(row.specificationId, e.target.value)
              }
              style={{
                background: empty ? "#fff7e6" : undefined,
              }}
            />
            {empty ? (
              <Typography.Text type="danger" style={{ minWidth: 36 }}>
                Tom
              </Typography.Text>
            ) : null}
            <Tooltip title="Kopiera från källspråk">
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyFromSource(row.specificationId)}
                disabled={!row.sourceValue}
              />
            </Tooltip>
          </Flex>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      destroyOnClose
      title={
        <Flex align="center" gap={8}>
          <TranslationOutlined />
          <span>Översätt specifikationer</span>
        </Flex>
      }
      footer={
        <Flex justify="space-between" align="center">
          <Typography.Text type="secondary">
            Tab hoppar till nästa fält
          </Typography.Text>
          <Flex gap={8}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              disabled={!activeProductId}
            >
              Uppdatera
            </Button>
            <Button
              icon={<OpenAIOutlined />}
              onClick={handleAiTranslate}
              disabled={
                !activeProductId || targetLocale === "sv" || rows.length === 0
              }
            >
              AI-översätt till målspråk
            </Button>
            <Button onClick={onClose}>Stäng</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Spara översättningar
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={6}>
          <Typography.Text type="secondary">Produkt</Typography.Text>
          <Flex gap={12} align="center" wrap="wrap">
            <div style={{ flex: 1, minWidth: 260 }}>
              <ProductSearchSelect
                resetKey={open ? `${productId}-open` : "closed"}
                placeholder="Sök och byt produkt..."
                onSelect={switchProduct}
              />
            </div>
            <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
              {activeProductLabel}
            </Typography.Text>
          </Flex>
        </Flex>

        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Flex align="center" gap={8}>
            <Typography.Text type="secondary">Från</Typography.Text>
            <Select
              style={{ width: 140 }}
              value={sourceLocale}
              options={languageOptions}
              onChange={handleSourceChange}
            />
            <Typography.Text type="secondary">→</Typography.Text>
            <Select
              style={{ width: 140 }}
              value={targetLocale}
              options={languageOptions}
              onChange={handleTargetChange}
            />
          </Flex>
          <Flex align="center" gap={12} style={{ minWidth: 220 }}>
            <Typography.Text>
              {completedCount} av {totalCount} klara
            </Typography.Text>
            <Progress
              percent={progressPercent}
              showInfo={false}
              style={{ width: 140, margin: 0 }}
            />
          </Flex>
        </Flex>

        <Table
          rowKey="specificationId"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          size="middle"
          scroll={{ y: 420 }}
          locale={{ emptyText: "Inga specifikationer att översätta" }}
        />
      </Flex>
    </Modal>
  );
};
